import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Col, DatePicker, Flex, Form, Input, Row, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPaperPlane, faTimes, faWarning } from "@fortawesome/free-solid-svg-icons";
import { mdlOffer } from "../../models/domain/Offer";
import { mdlOfferHistory } from "../../models/domain/OfferHistory";
import dayjs from "dayjs";
import { useMutation, useQuery } from "react-query";
import { OfferService } from "../../services/OfferService";
import { mdlHotelTypesForSejourOfferItem } from "../../models/domain/HotelTypesForSejourOfferItem";
import { mdlOfferAnswer } from "../../models/domain/OfferAnswer";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { useAppSelector } from "../../store/Hooks";
import { enmOfferAnswerType } from "../../models/enums/OfferAnswerType";

export type OfferDetailProps = {
    offer?: mdlOffer;
    callback?: Function;
}

export const OfferDetail = memo(function OfferDetail(props: OfferDetailProps) {
    const siteStore = useAppSelector((store) => store.site);

    const [hotelTypes, setHotelTypes] = useState<mdlHotelTypesForSejourOfferItem[]>([]);
    const [hotelProperties, setHotelProperties] = useState<mdlHotelTypesForSejourOfferItem[]>([]);
    const [offerDetail, setOfferDetail] = useState<mdlOffer>();
    const [offerHistory, setOfferHistory] = useState<mdlOfferHistory[]>([]);

    const [answerType, setAnswerType] = useState<enmOfferAnswerType>(enmOfferAnswerType.None);

    const [form] = Form.useForm<mdlOfferAnswer>();

    const { t } = useTranslation();
    const handleClickSend = async () => {
        if (!form.getFieldValue("price") || !form.getFieldValue("optionalDate")) {
            toast.error(t("offer.pleaseFillOutYourOfferRequestAndOptionDate"))
            return;
        }
        setAnswerType(enmOfferAnswerType.Send);
        form.submit();
    };
    const handleClickIgnore = async () => {
        setAnswerType(enmOfferAnswerType.Ignore);
        form.submit();
    };
    const handleClickAddToWatchList = () => {
        setAnswerType(enmOfferAnswerType.Watch);
        form.submit();
    };
    const handleClickClose = () => {
        dispatchClose(false);
    };
    const dispatchClose = (refreshList: boolean) => {
        props.callback && props.callback(refreshList);
    };

    const { mutateAsync: answerOffer, isLoading: mutateAnswerLoading, data: mutatedAnswerData } = useMutation(
        async (data: mdlOfferAnswer) => {
            return OfferService.Answer({
                answers: [data],
                paxId: offerDetail?.paximumId
            })
        },
        {
            onMutate(variables) {
                variables.offerId = offerDetail?.id;
                variables.isConfirm = answerType;
                variables.hotelUserId = offerDetail?.hotelUserId;
                variables.userName = siteStore.user?.userName;
                variables.currency = offerDetail?.currency;
                if (variables.optionalDate) {
                    const optionalDateObject = dayjs(variables.optionalDate);
                    variables.optionalDateValue = [optionalDateObject.year(), optionalDateObject.month() + 1, optionalDateObject.date(), optionalDateObject.hour(), optionalDateObject.minute()];
                }
            },
            onError: error => console.log(error),
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    if (data.body?.isSuccessful === true) {
                        toast.success(t("common.success"));
                        form.resetFields();
                        dispatchClose(true);
                    }
                    else {
                        toast.error(data.body?.message || t("common.errorSaving"));
                    }
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            }
        }
    )

    const {
        data,
        isLoading,
        refetch: getOfferDetail
    } = useQuery(
        ["getOfferDetail"],
        async () => {
            if (props.offer) {
                return OfferService.Detail({
                    isRead: props.offer.isReaded,
                    offerId: props.offer.id,
                    offerNo: props.offer.offerNo,
                    paxId: props.offer.paximumId,
                    sId: props.offer.serviceId,
                    pageSize: 0
                })
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!props.offer,
            onSuccess(data) {
                if (data?.body?.data) {
                    setHotelTypes(data.body.data.hotelTypes || []);
                    setHotelProperties(data.body.data.hotelProperties || []);
                    setOfferDetail(data.body.data.firstOffer);
                    setOfferHistory(data.body.data.offerHistory || []);

                    form.resetFields();
                    form.setFieldsValue({
                        price: data.body.data.firstOffer?.price,
                        optionalDate: dayjs(data.body.data.firstOffer?.resStartDate || new Date())
                    });
                }
            }
        }
    )

    useEffect(() => {
        if (props.offer) {
            getOfferDetail();
        }
    }, [props]);
    
    const setRemainingTimes = (dateNow:Date, dateOfferEnd:Date) => {
        var totMinute = (dateOfferEnd.getTime() - dateNow.getTime()) / 1000 / 60;
        var day = (totMinute / (60 * 24)).toString();
        var _day = totMinute % (60 * 24);
        var hour = (_day / 60).toString();
        var _hour = (_day % 60).toString();
        return [parseInt(day, 10), parseInt(hour, 10), parseInt(_hour, 10)];
    }
    
    const getRemainingTimes = (offer:mdlOffer | undefined) => {
        if(offer == undefined || offer.dayHourMin == undefined || offer.dayHourMin == null) {
            return null;
        }
        var dateNow = new Date(new Date().getTime());
        var dateOfferEnd = new Date(dateNow.setDate(dateNow.getDate() + parseInt(offer.dayHourMin[0], 10)));
        dateOfferEnd = new Date(dateOfferEnd.setHours(dateNow.getHours() + parseInt(offer.dayHourMin[1], 10)));
        dateOfferEnd = new Date(dateOfferEnd.setMinutes(dateNow.getMinutes() + parseInt(offer.dayHourMin[2], 10)));
        return setRemainingTimes(new Date(), dateOfferEnd);
        
    }
    const checkTOut = (offer:mdlOffer | undefined) => {
        if(offer == undefined || offer.dayHourMin == undefined || offer.dayHourMin == null) {
            return false;
        }
        if (getRemainingTimes(offer)?.some(n => n < 0)) {
            return true;
        }
        return false;
    }



    return (
        <>
            <Spin spinning={isLoading || mutateAnswerLoading}>
                <div className="card card-bordered">
                    <div className="card-inner">
                        <div className="team">
                            <div className="card-inner">
                                <div className="row">
                                    <h6 className="overline-title mb-2">{t("offer.hotelThemes")}</h6>
                                    <div className="row">
                                        <Checkbox.Group onChange={() => { }} value={offerDetail?.hotelTypes}>
                                            <Row >
                                                {
                                                    hotelTypes?.map((hotelType: mdlHotelTypesForSejourOfferItem) => (
                                                        <Col span={8}>
                                                            <Checkbox value={hotelType.id.toString()} name="hotelType" onClick={() => { return; }}>{hotelType.name}</Checkbox>
                                                        </Col>
                                                    ))
                                                }
                                            </Row>
                                        </Checkbox.Group>
                                    </div>
                                </div>
                                <div className="row mt-35">
                                    <h6 className="overline-title mb-2">{t("offer.hotelProperties")}</h6>
                                    <div className="row">
                                        <Checkbox.Group onChange={() => { }} value={offerDetail?.hotelProperties}>
                                            <Row>
                                                {
                                                    hotelProperties?.map((hotelProperty: mdlHotelTypesForSejourOfferItem) => (
                                                        <Col span={8}>
                                                            <Checkbox value={hotelProperty.id.toString()} name="hotelProperty" onClick={() => { return; }}>{hotelProperty.name}</Checkbox>
                                                        </Col>
                                                    ))
                                                }
                                            </Row>
                                        </Checkbox.Group>
                                    </div>
                                </div>
                                <div className="row mt-35">
                                    <h6 className="overline-title mb-2">{t("offer.reservationInfo")}</h6>
                                    <div className="row">
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.roomName")}</span>
                                            <span>{offerDetail?.roomName}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.roomType")}</span>
                                            <span>{offerDetail?.roomTypeName}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.accomName")}</span>
                                            <span>{offerDetail?.accomName}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.priceType")}</span>
                                            <span>{offerDetail?.priceTypeStr}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.requestedPrice")}</span>
                                            <span>{offerDetail?.priceWithCurr}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.checkIn")}</span>
                                            <span>{offerDetail?.resStartDate && dayjs(offerDetail?.resStartDate).format("DD.MM.YYYY")}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.checkOut")}</span>
                                            <span>{offerDetail?.resEndDate && dayjs(offerDetail?.resEndDate).format("DD.MM.YYYY")}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.night")}</span>
                                            <span>{offerDetail?.night}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.customerArrivalTime")}</span>
                                            <span>{offerDetail?.customerArrTime && dayjs(offerDetail?.customerArrTime).format("DD.MM.YYYY HH:mm")}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.agency")}</span>
                                            <span>{offerDetail?.agencyName}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.operator")}</span>
                                            <span>{offerDetail?.operatorName}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.nationality")}</span>
                                            <span>{offerDetail?.nationalityName}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.offerNo")}</span>
                                            <span>{offerDetail?.offerNo}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.groupReservation")}</span>
                                            <span>{offerDetail?.isGroup ? t("common.yes") : t("common.no")}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.roomCount")}</span>
                                            <span>{offerDetail?.roomCount}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.personCount")}</span>
                                            <span>{offerDetail?.adult}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.childCount")}</span>
                                            <span>{offerDetail?.child}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.childAges")}</span>
                                            <span>{(offerDetail?.childAge1 || "-") + "/" + (offerDetail?.childAge2 || "-")}</span>
                                        </div>
                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                            <span className="sub-text">{t("offer.infantCount")}</span>
                                            <span>{offerDetail?.infant}</span>
                                        </div>
                                    </div>
                                </div>
                                {
                                    offerDetail?.description &&
                                    <div className="mt-35 alert alert-primary alert-icon ps-3">
                                        <span className="pe-3"><FontAwesomeIcon icon={faWarning}></FontAwesomeIcon></span>
                                        <strong>{offerDetail?.description}</strong>
                                    </div>
                                }
                                <div className="row mt-35">
                                    <h6 className="overline-title mb-2">{t("offer.yourOfferRequest")}</h6>
                                    <Form<mdlOfferAnswer>
                                        form={form}
                                        name="offerAnswerForm"
                                        layout="vertical"
                                        autoComplete="off"
                                        onFinish={async (values) => await answerOffer(values)}
                                        className="row"
                                    >
                                        <Form.Item<mdlOfferAnswer>
                                            label={t("offer.yourOffer")}
                                            name="price"
                                            className="col"
                                            required
                                        >
                                            <Input type="number" step="0.01" suffix={offerDetail?.currency} placeholder={t("offer.yourOffer")} />
                                        </Form.Item>
                                        <Form.Item<mdlOfferAnswer>
                                            label={t("offer.optionTime")}
                                            name="optionalDate"
                                            className="col"
                                            required
                                        >
                                            <DatePicker format={"DD.MM.YYYY"} placeholder={t("offer.optionTime")} style={{ width: "100%" }} />
                                        </Form.Item>
                                        <Form.Item<mdlOfferAnswer>
                                            label={t("offer.note")}
                                            name="note"
                                            className="col"
                                        >
                                            <Input placeholder={t("offer.note")} />
                                        </Form.Item>
                                    </Form>
                                </div>
                                {
                                    offerHistory && offerHistory.length > 0 && <div className="row mt-35">
                                        <h6 className="overline-title mb-2">{t("offer.history")}</h6>
                                        <div className="row">
                                            <div className="nk-tb-list is-compact">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">{t("offer.date")}</th>
                                                            <th scope="col">{t("offer.user")}</th>
                                                            <th scope="col">{t("offer.price")}</th>
                                                            <th scope="col">{t("offer.optionTime")}</th>
                                                            <th scope="col">{t("offer.status")}</th>
                                                            <th scope="col">{t("offer.note")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            offerHistory?.map((offerHistory: mdlOfferHistory) => (
                                                                <>
                                                                    <tr>
                                                                        <th scope="row">{offerHistory.dateCreated}</th>
                                                                        <td>{offerHistory.userName}</td>
                                                                        <td>{offerHistory.price} {offerHistory.currency} / {offerHistory.priceType}</td>
                                                                        <td>{offerHistory.optionalDate}</td>
                                                                        <td>{offerHistory.confirm}</td>
                                                                        <td>{offerHistory.note}</td>
                                                                    </tr>
                                                                </>
                                                            ))
                                                        }
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                }
                                <Flex gap="small" wrap justify="flex-end" className="mt-35">
                                    {
                                        ( checkTOut(props.offer) ||  (props.offer?.offered != undefined && props.offer?.offered) || props.offer?.winnerOfferDate != undefined || props.offer?.cancelDate != undefined) ?
                                        <>
                                            <button type="button" className="btn btn-secondary" onClick={handleClickClose}>{t("common.close")}</button>
                                        </>
                                        :
                                        <>
                                            {
                                                props.offer?.isAnswered == enmOfferAnswerType.Send || props.offer?.isAnswered == enmOfferAnswerType.Ignore &&
                                                <>
                                                    <button type="button" className="btn btn-primary" onClick={handleClickSend}><FontAwesomeIcon icon={faPaperPlane} className="me-1" />{t("offer.send")}</button>
                                                    <button type="button" className="btn btn-secondary" onClick={handleClickClose}>{t("common.close")}</button>
                                                </>
                                            }
                                            {
                                                props.offer?.isAnswered == enmOfferAnswerType.Watch &&
                                                <>
                                                    <button type="button" className="btn btn-primary" onClick={handleClickSend}><FontAwesomeIcon icon={faPaperPlane} className="me-1" />{t("offer.send")}</button>
                                                    <button type="button" className="btn btn-danger" onClick={handleClickIgnore}><FontAwesomeIcon icon={faTimes} className="me-1" />{t("offer.ignore")}</button>
                                                    <button type="button" className="btn btn-secondary" onClick={handleClickClose}>{t("common.close")}</button>
                                                </>
                                            }
                                            {
                                                props.offer?.isAnswered == enmOfferAnswerType.None &&
                                                <>
                                                    <button type="button" className="btn btn-primary" onClick={handleClickSend}><FontAwesomeIcon icon={faPaperPlane} className="me-1" />{t("offer.send")}</button>
                                                    <button type="button" className="btn btn-danger" onClick={handleClickIgnore}><FontAwesomeIcon icon={faTimes} className="me-1" />{t("offer.ignore")}</button>
                                                    <button type="button" className="btn btn-warning" onClick={handleClickAddToWatchList}><FontAwesomeIcon icon={faEye} className="me-1" />{t("offer.watch")}</button>
                                                    <button type="button" className="btn btn-secondary" onClick={handleClickClose}>{t("common.close")}</button>
                                                </>
                                            }
                                        </>
                                    }
                                </Flex>
                            </div>
                        </div>
                    </div>
                </div>
            </Spin>
        </>
    )
})